import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import {
  BorderStyleEnum,
  BorderType,
  BorderTypeEnum,
} from 'common/types/styleTypes'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { Repeat04Icon } from 'src/editor/shared/icons/repeat-04-icon'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import {
  ButtonSizeEnum,
  GhostIconButton,
} from 'src/editor/shared/ui-lib/buttons'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import {
  OptionSingleValueType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { TypedTFunctionInterface } from 'src/shared/i18next/types/typed-t-function-interface'
import BorderRadius from './BorderRadius'
import MobileIconUi from './ui/MobileIconUi'

const getBorderStyleChoices = (t: TypedTFunctionInterface) => [
  {
    label: t('settings_styles.border.style.choices.none'),
    value: BorderStyleEnum.none,
  },
  {
    label: t('settings_styles.border.style.choices.solid'),
    value: BorderStyleEnum.solid,
  },
  {
    label: t('settings_styles.border.style.choices.dotted'),
    value: BorderStyleEnum.dotted,
  },
  {
    label: t('settings_styles.border.style.choices.dashed'),
    value: BorderStyleEnum.dashed,
  },
]

const getBorderTypeChoices = (t: TypedTFunctionInterface) => [
  {
    label: t('Full Border'),
    value: BorderTypeEnum.fullBorder,
  },
  {
    label: t('Bottom Only'),
    value: BorderTypeEnum.bottomOnly,
  },
  {
    label: t('Top Only'),
    value: BorderTypeEnum.topOnly,
  },
  {
    label: t('Top & Bottom'),
    value: BorderTypeEnum.topAndBottom,
  },
]

type BorderKeyType = 'width' | 'style' | 'radius' | 'color' | 'type'

type BorderProps = {
  border?: Partial<BorderType>
  defaultBorder?: Partial<BorderType>
  mobileDefaultBorder?: Partial<BorderType>
  update: (border: Partial<BorderType>) => void
  mobileUpdate?: (border: Partial<BorderType>) => void
  showGroupTitle?: boolean
  groupTitle?: string
  groupTitleAfterChildren?: React.ReactNode
  max?: number
  withoutType?: boolean
  mobileBorder?: Partial<BorderType>
}

function Border({
  border = {},
  defaultBorder,
  mobileDefaultBorder,
  update,
  showGroupTitle = true,
  groupTitle = 'settings_styles.border_style.group_title',
  max = 10,
  withoutType,
  mobileBorder,
  mobileUpdate,
}: BorderProps) {
  const { isMobile } = useDeviceModeContext()
  const { t } = useTypedTranslation()

  const currentBorder = isMobile ? mobileBorder || border : border
  const currentUpdateBorder = isMobile && mobileUpdate ? mobileUpdate : update

  function updateBorder(prop: BorderKeyType) {
    return (value: string | number | null) => {
      currentUpdateBorder({
        ...currentBorder,
        [prop]: value,
      })
    }
  }

  function updateBorderStyle(valueOption: OptionSingleValueType) {
    if (valueOption) {
      const styleValue = valueOption.value as BorderStyleEnum
      if (
        (!currentBorder.style ||
          currentBorder.style === BorderStyleEnum.none) &&
        valueOption.value !== BorderStyleEnum.none
      ) {
        currentUpdateBorder({
          ...currentBorder,
          style: styleValue,
          width: 1,
          color: 'rgba(0, 0, 0, 0.5)',
        })
      } else if (
        currentBorder.style !== BorderStyleEnum.none &&
        valueOption.value === BorderStyleEnum.none
      ) {
        const updatedBorder = {
          ...currentBorder,
          style: styleValue,
        }
        delete updatedBorder.color
        delete updatedBorder.width
        currentUpdateBorder(updatedBorder)
      } else {
        currentUpdateBorder({
          ...currentBorder,
          style: styleValue,
        })
      }
    }
  }

  function updateBorderType(
    valueOption: OptionSingleValueType<BorderTypeEnum>,
  ) {
    if (valueOption) {
      currentUpdateBorder({
        ...currentBorder,
        type: valueOption.value,
      })
    }
  }

  const onResetStyles = () => {
    if (isMobile && mobileUpdate) {
      mobileUpdate(mobileDefaultBorder || {})
    } else {
      update(defaultBorder || {})
    }
  }

  const borderStyleChoices = getBorderStyleChoices(t)
  const borderTypeChoices = getBorderTypeChoices(t)

  return (
    <>
      {showGroupTitle && (
        <GroupTitle
          afterChildren={
            <GhostIconButton
              size={ButtonSizeEnum.Small}
              onClick={onResetStyles}
              icon={<Repeat04Icon />}
            />
          }
        >
          {groupTitle}
        </GroupTitle>
      )}
      <BorderRadius
        label="settings_styles.border_radius.label"
        labelIcon={isMobile ? <MobileIconUi /> : null}
        border={currentBorder}
        update={currentUpdateBorder}
      />
      <SelectNew
        labelText="settings_styles.border_style.label"
        options={borderStyleChoices}
        selectedOption={
          borderStyleChoices.find(
            borderStyle => borderStyle.value === currentBorder.style,
          ) || borderStyleChoices[0]
        }
        labelIcon={isMobile ? <MobileIconUi /> : undefined}
        onChange={updateBorderStyle}
        isMulti={false}
      />
      {currentBorder.style && currentBorder.style !== 'none' && (
        <>
          <ColorPickerControl
            color={currentBorder.color}
            update={updateBorder('color')}
            label="settings_styles.border_color.label"
            singleColorMode
            isClearable
          />
          <RangeElement
            label="settings_styles.border_width.label"
            labelIcon={isMobile ? <MobileIconUi /> : null}
            value={currentBorder.width as number}
            onChange={updateBorder('width')}
            max={max}
          />
          {!withoutType && (
            <SelectNew
              labelText="settings_styles.border.type"
              options={borderTypeChoices}
              selectedOption={
                borderTypeChoices.find(
                  borderType => borderType.value === currentBorder.type,
                ) || borderTypeChoices[0]
              }
              labelIcon={isMobile ? <MobileIconUi /> : undefined}
              onChange={updateBorderType}
              isMulti={false}
            />
          )}
        </>
      )}
    </>
  )
}

export default Border
